import { mcn } from "@/utils/mergeClassNames";
import { useState } from "react";
import Label, { LabelProps } from "../Label";
import Dropdown from "./Dropdown";

type DatePickerProps<T> = {
  id?: string;
  className?: string;
  labelProps?: LabelProps;
  onChange: (date?: Date) => void;
  disabled?: boolean;
  date?: Date;
  showFutureYears: boolean;
};

function DatePicker<T>(props: DatePickerProps<T>) {
  const [selectedDate, setSelectedDate] = useState(props.date ?? new Date());

  const disabled: boolean = props.disabled ?? false;

  const currentYear = new Date().getFullYear();
  const yearLength = props.showFutureYears ? 120 : 100;
  const years = Array.from(
    { length: yearLength },
    (_, i) => currentYear - 100 + i
  ).reverse();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const daysInMonth = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    0
  ).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleYearChange = (year?: number) => {
    if (year !== undefined) {
      const newDate = new Date(selectedDate);
      newDate.setFullYear(year);
      setSelectedDate(newDate);
      props.onChange(newDate);
    }
  };

  const handleMonthChange = (month?: number) => {
    if (month != null) {
      const newDate = new Date(selectedDate);
      newDate.setMonth(month - 1);
      setSelectedDate(newDate);
      props.onChange(newDate);
    }
  };

  const handleDayChange = (day?: number) => {
    if (day != null) {
      const newDate = new Date(selectedDate);
      newDate.setDate(day);
      setSelectedDate(newDate);
      props.onChange(newDate);
    }
  };
  return (
    <div className={mcn("relative", props.className)}>
      {props.labelProps && (
        <Label
          {...props.labelProps}
          className={mcn(props.labelProps.className, "mb-1")}
        />
      )}

      <div className="grid grid-cols-3">
        <Dropdown
          className="col-span-1 tablet:col-span-1"
          onChange={(item) => handleYearChange(item?.data)}
          items={years.map((year) => ({
            text: year.toString() === new Date().getFullYear().toString() ? "" : year.toString(),
            id: year,
            data: year,
            preSelected:
              props.date !== null && selectedDate.getFullYear() === year
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Year",
            className: "sr-only",
          }}
        />
        <Dropdown
          className="ml-sm col-span-1 tablet:col-span-1"
          onChange={(item) => handleMonthChange(item?.data)}
          items={months.map((month) => ({
            text: month.toString() === (new Date().getMonth() + 1).toString() ? "" : month.toString(),
            id: month,
            data: month,
            preSelected:
              props.date !== null && selectedDate.getMonth() + 1 === month
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Month",
            className: "sr-only",
          }}
        />
        <Dropdown
          className="ml-sm col-span-1 tablet:col-span-1"
          onChange={(item) => handleDayChange(item?.data)}
          items={days.map((day) => ({
            text: day.toString() === new Date().getDate().toString() ? "" : day.toString(),
            id: day,
            data: day,
            preSelected:
              props.date !== null && selectedDate.getDate() === day
                ? true
                : false,
          }))}
          disabled={disabled}
          labelProps={{
            text: "Day",
            className: "sr-only",
          }}
        />
      </div>
    </div>
  );
}

export default DatePicker;
